import { apiClient, idempotentConfig, tenantHub, unwrap } from './client';
import type {
  ApiEnvelope,
  BindingUpsertRequest,
  BindingView,
  EntitlementsView,
  FamilyCatalogue,
  FamilyCatalogueEntry,
  ProviderView,
} from './types';

/**
 * Capability registry reads + per-hub binding administration (the EXISTING capability-fabric
 * FabricRegistry endpoints, contracts/proposed/openapi/product-api.v1.yaml). The family catalogue is
 * the closed set of 12 (integration-fabric §2.1); providers resolve by (family, version, providerId);
 * bindings and entitlements are hub-scoped (the `{hub}` path segment MUST equal the caller's own hub,
 * else the server rejects 403 — never a cross-hub read via the path). Every call unwraps the
 * `{status, traceId, data}` envelope; callers handle failures via `normalizeApiError` (errors.ts).
 */

const enc = (s: string) => encodeURIComponent(s);

/** GET /v1/capabilities/families — the canonical 12 families (always available; static catalogue). */
export async function listCapabilityFamilies(): Promise<FamilyCatalogueEntry[]> {
  const { data } = await apiClient.get<ApiEnvelope<FamilyCatalogue>>('/v1/capabilities/families');
  return unwrap(data).families;
}

/**
 * GET /v1/capabilities/providers/{family}/{version}/{providerId} — one registered provider
 * descriptor. There is deliberately NO "list providers by family" endpoint in the contract, so the
 * registry resolves a provider only when a binding (or the admin) names its id.
 */
export async function getCapabilityProvider(
  family: string,
  version: number,
  providerId: string,
): Promise<ProviderView> {
  const { data } = await apiClient.get<ApiEnvelope<ProviderView>>(
    `/v1/capabilities/providers/${enc(family)}/${version}/${enc(providerId)}`,
  );
  return unwrap(data);
}

/**
 * GET /v1/tenants/{hub}/capability-bindings — the hub's ENABLED provider bindings (family order).
 * Contract: `ApiResponseBindingList.data` is a BARE ARRAY of BindingView (verified against the
 * OpenAPI — NOT a `{bindings:[…]}` wrapper). Still guard the unwrap against a non-array.
 */
export async function listCapabilityBindings(): Promise<BindingView[]> {
  const { data } = await apiClient.get<ApiEnvelope<BindingView[]>>(
    `/v1/tenants/${enc(tenantHub())}/capability-bindings`,
  );
  const body = unwrap(data);
  return Array.isArray(body) ? body : [];
}

/**
 * PUT /v1/tenants/{hub}/capability-bindings/{family}/{version} — bind (config-validated,
 * entitlement-gated). 422 on unregistered provider / config-schema violation / missing entitlement.
 */
export async function putCapabilityBinding(
  family: string,
  version: number,
  body: BindingUpsertRequest,
  idemKey?: string,
): Promise<BindingView> {
  const { data } = await apiClient.put<ApiEnvelope<BindingView>>(
    `/v1/tenants/${enc(tenantHub())}/capability-bindings/${enc(family)}/${version}`,
    body,
    idempotentConfig(idemKey),
  );
  return unwrap(data);
}

/** GET /v1/tenants/{hub}/entitlements — the hub's ACTIVE (unexpired) entitlement classes. */
export async function getEntitlements(): Promise<EntitlementsView> {
  const { data } = await apiClient.get<ApiEnvelope<EntitlementsView>>(
    `/v1/tenants/${enc(tenantHub())}/entitlements`,
  );
  return unwrap(data);
}
